import { match as pathMatch } from 'path-to-regexp';
import type { RemoteModuleConfig } from '../types';
import { calculateRouteSpecificity } from './utils';

/**
 * Module Resolution Utilities
 *
 * Provides functions to resolve module URLs dynamically based on route patterns
 * and PSP context (environment, route path, active entity, etc.).
 */

/**
 * Picks the appropriate module route from an app's moduleRoutes array based on the current route path.
 * Uses path-to-regexp for human-readable route matching.
 *
 * When multiple routes match, the most specific/restrictive pattern is selected.
 * For example, "/reports/:id/details" will be chosen over "/:all" when both match.
 *
 * @param remoteAppConfig - The remote app configuration
 * @param currentPath - The current route pathname
 * @returns The matching ModuleRoute, or the default route (pattern '/') if no match found
 */
export function pickModuleRoute(
  moduleConfig: RemoteModuleConfig,
  currentPath: string
): { pattern: string; resolver: string } {
  const baseRouterPath = moduleConfig.basePath;

  // Get the path within the app by removing the app's base path from the current URL.
  // Example: "/reports/123/details" currentPath with base "/reports" yields "/123/details"
  const relativePath = currentPath.replace(baseRouterPath, '') || '/';

  // Find all matching routes
  const matchingRoutes: Array<{
    route: { pattern: string; resolver: string };
    specificity: number;
  }> = [];

  const moduleRoutes = moduleConfig.moduleRoutes || [];

  for (const route of moduleRoutes) {
    const matcher = pathMatch(route.pattern, { end: false });
    if (matcher(relativePath)) {
      const specificity = calculateRouteSpecificity(route.pattern);
      matchingRoutes.push({ route, specificity });
    }
  }

  // If we have matches, return the most specific one
  if (matchingRoutes.length > 0) {
    // Sort by specificity (descending) and return the most specific match
    matchingRoutes.sort((a, b) => b.specificity - a.specificity);
    return matchingRoutes[0].route;
  }

  // Fallback to the default route (pattern '/')
  const defaultRoute = moduleRoutes.find((r) => r.pattern === '/');
  if (!defaultRoute) {
    throw new Error(
      `[moduleResolver] No default route (pattern '/') found for "${moduleConfig.id}". At least one route with pattern '/' is required.`
    );
  }

  return defaultRoute;
}

/**
 * Resolves the module URL for a given module configuration.
 * Picks the appropriate route based on the current path.
 * Note: The resolver is already resolved (it's a string), so we just return it.
 *
 * @param currentPath - The current route pathname
 * @param moduleConfig - The module configuration with moduleRoutes
 * @returns Promise resolving to the module URL and the matched route
 */
export async function resolveModuleUrl(
  currentPath: string,
  moduleConfig: RemoteModuleConfig
): Promise<{ url: string; route: { pattern: string; resolver: string } }> {
  // Match the current route path against the module's route patterns
  // This finds all matching routes and selects the most specific/restrictive one.
  // For example, "/reports/:id/details" will be chosen over "/:all" when both match.
  // If no match is found, it falls back to the default route (pattern '/')
  const route = pickModuleRoute(moduleConfig, currentPath);

  // The resolver is already resolved (it's a string), so we can use it directly
  return { url: route.resolver, route };
}
